import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Calendar, Heart, Star, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { useNavigate } from 'react-router-dom';
import { checkDailyManifestationAvailable, hasReceivedTodaysManifestation } from '../lib/supabase';

interface Notification {
  id: string;
  type: 'manifestation' | 'gratitude' | 'analytics' | 'system';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  link?: string;
  tabName?: string;
  icon: React.ComponentType<any>;
  color: string;
}

export const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications when user is logged in
  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  // Update unread count when notifications change
  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.read).length);
  }, [notifications]);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      // Check for daily manifestation
      const manifestationAvailable = await checkDailyManifestationAvailable(user.id);
      const receivedToday = await hasReceivedTodaysManifestation(user.id);

      let notificationsList: Notification[] = [];

      // Add daily manifestation notification if available and not received
      if (manifestationAvailable && !receivedToday) {
        notificationsList.push({
          id: `manifestation-${Date.now()}`,
          type: 'manifestation',
          title: t('profile.dailyManifestationReady'),
          message: t('profile.newManifestationWaiting'),
          timestamp: new Date(),
          read: false,
          link: '/profile',
          tabName: 'cards',
          icon: Star,
          color: 'bg-purple-100 text-purple-600'
        });
      }

      // Check if user has completed gratitude practice today
      const gratitudeDone = localStorage.getItem(`gratitude_completed_${new Date().toDateString()}`);
      if (!gratitudeDone) {
        notificationsList.push({
          id: `gratitude-${Date.now()}`,
          type: 'gratitude',
          title: 'Daily Gratitude Practice',
          message: 'Take a moment to reflect on what you\'re grateful for today',
          timestamp: new Date(),
          read: false,
          link: '/profile',
          tabName: 'gratitude',
          icon: Heart,
          color: 'bg-pink-100 text-pink-600'
        });
      }

      // Weekly analytics notification (every Monday)
      const today = new Date();
      if (today.getDay() === 1) { // Monday
        const analyticsViewed = localStorage.getItem(`analytics_viewed_${today.toISOString().split('T')[0]}`);
        if (!analyticsViewed) {
          notificationsList.push({
            id: `analytics-${Date.now()}`,
            type: 'analytics',
            title: 'Weekly Insights Available',
            message: 'Your manifestation analytics for last week are ready to view',
            timestamp: new Date(),
            read: false,
            link: '/profile',
            tabName: 'analytics-insights',
            icon: BarChart3,
            color: 'bg-blue-100 text-blue-600'
          });
        }
      }

      // Add system notifications (for demonstration)
      notificationsList.push({
        id: 'system-1',
        type: 'system',
        title: 'New Manifestation Category',
        message: 'Explore our new "Career Success" manifestation category',
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
        read: true,
        link: '/profile',
        tabName: 'cards',
        icon: Calendar,
        color: 'bg-amber-100 text-amber-600'
      });

      setNotifications(notificationsList);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true } 
          : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    setShowDropdown(false);
    
    // For analytics notifications, mark as viewed
    if (notification.type === 'analytics') {
      localStorage.setItem(`analytics_viewed_${new Date().toISOString().split('T')[0]}`, 'true');
    }

    // Navigate to the correct page with state instead of query params
    if (notification.link) {
      navigate(notification.link, { 
        state: { activeTab: notification.tabName } 
      });
    }
  };

  const removeNotification = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-purple-600 transition-colors duration-200"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <h3 className="font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <button 
                onClick={markAllAsRead}
                className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded text-white transition-colors duration-200"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <Bell size={24} className="mx-auto mb-2 text-gray-400" />
                <p>No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`block px-4 py-3 hover:bg-gray-50 transition-colors duration-200 cursor-pointer ${
                      !notification.read ? 'bg-purple-50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${notification.color}`}>
                        <notification.icon size={16} />
                      </div>
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                        <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {notification.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <button
                        onClick={(e) => removeNotification(notification.id, e)}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 text-center">
            <button
              onClick={() => {
                navigate('/settings', { state: { activeTab: 'notifications' } });
                setShowDropdown(false);
              }}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              Manage notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};